import React, { useState, useCallback, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { 
  Upload, 
  FileText, 
  Image as ImageIcon, 
  Camera,
  Loader2, 
  CheckCircle2, 
  AlertTriangle,
  Sparkles,
  Zap,
  FileSearch,
  ArrowRight,
  X,
  Eye,
  Download,
  Plus,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// File type icons
const getFileIcon = (type) => {
  if (type?.includes('pdf')) return <FileText className="h-8 w-8 text-red-500" />;
  if (type?.includes('image')) return <ImageIcon className="h-8 w-8 text-blue-500" />;
  return <FileText className="h-8 w-8 text-gray-500" />;
};

// Field display component
const ExtractedField = ({ label, value, confidence }) => {
  if (!value) return null;
  
  return (
    <div className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0">
      <div className="flex-1">
        <span className="text-sm text-gray-500">{label}</span>
        <p className="font-medium text-gray-900">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</p>
      </div>
      {confidence && (
        <Badge variant="outline" className="ml-2 text-xs">
          {confidence}%
        </Badge>
      )}
    </div>
  );
};

/**
 * FastOCRImport Component
 * 
 * Provides a fast path to populate inspection and report fields from:
 * - Inspection sheets (scanned documents)
 * - PDF reports
 * - Site photos
 */
export default function FastOCRImport({ 
  onDataExtracted, 
  onInspectionCreated,
  existingInspectionId = null
}) {
  const { token } = useContext(AuthContext);
  
  // State
  const [files, setFiles] = useState([]);
  const [extractionMode, setExtractionMode] = useState('auto');
  const [useAI, setUseAI] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('upload');
  
  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter(f => 
      f.type.includes('image') || f.type.includes('pdf')
    );
    
    if (validFiles.length !== droppedFiles.length) {
      toast.warning('Some files were skipped. Only images and PDFs are supported.');
    }
    
    setFiles(prev => [...prev, ...validFiles].slice(0, 10));
  }, []);
  
  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles].slice(0, 10));
  };
  
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // Process files
  const processFiles = async () => {
    if (files.length === 0) {
      toast.error('Please select at least one file');
      return;
    }
    
    setProcessing(true);
    setProgress(0);
    setError(null);
    setResults(null);
    
    try {
      if (files.length === 1) {
        // Single file extraction
        const formData = new FormData();
        formData.append('file', files[0]);
        formData.append('extraction_mode', extractionMode);
        formData.append('use_ai', useAI.toString());
        
        setProgress(30);
        
        const response = await fetch(`${API_URL}/api/ocr-import/extract`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        setProgress(80);
        
        const data = await response.json();
        
        if (data.success) {
          setResults(data);
          setActiveTab('results');
          toast.success(`Extracted ${data.fields_count || 0} fields from document`);
          
          if (onDataExtracted) {
            onDataExtracted(data.mapped_fields);
          }
        } else {
          setError(data.error || 'Extraction failed');
          toast.error(data.error || 'Extraction failed');
        }
      } else {
        // Multiple file extraction
        const formData = new FormData();
        files.forEach(f => formData.append('files', f));
        formData.append('use_ai', useAI.toString());
        
        setProgress(30);
        
        const response = await fetch(`${API_URL}/api/ocr-import/extract-multiple`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        setProgress(80);
        
        const data = await response.json();
        
        if (data.success) {
          setResults({
            ...data,
            mapped_fields: data.merged_fields
          });
          setActiveTab('results');
          toast.success(`Extracted ${data.fields_count || 0} fields from ${data.files_processed} files`);
          
          if (onDataExtracted) {
            onDataExtracted(data.merged_fields);
          }
        } else {
          setError(data.error || 'Batch extraction failed');
          toast.error(data.error || 'Batch extraction failed');
        }
      }
    } catch (err) {
      setError(err.message);
      toast.error(`Processing error: ${err.message}`);
    } finally {
      setProcessing(false);
      setProgress(100);
    }
  };
  
  // Create inspection from results
  const createInspection = async () => {
    if (!results?.mapped_fields) {
      toast.error('No extracted data available');
      return;
    }
    
    setProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/ocr-import/create-inspection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(results.mapped_fields)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Inspection created successfully!');
        
        if (onInspectionCreated) {
          onInspectionCreated(data.inspection_id, data.inspection);
        }
      } else {
        toast.error(data.detail || 'Failed to create inspection');
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };
  
  // Populate existing inspection
  const populateInspection = async () => {
    if (!results?.mapped_fields || !existingInspectionId) {
      toast.error('Missing data or inspection ID');
      return;
    }
    
    setProcessing(true);
    
    try {
      const response = await fetch(
        `${API_URL}/api/ocr-import/populate-inspection/${existingInspectionId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(results.mapped_fields)
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Updated ${data.fields_updated?.length || 0} fields`);
        
        if (onDataExtracted) {
          onDataExtracted(results.mapped_fields);
        }
      } else {
        toast.error(data.detail || 'Failed to populate inspection');
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };
  
  // Reset
  const reset = () => {
    setFiles([]);
    setResults(null);
    setError(null);
    setProgress(0);
    setActiveTab('upload');
  };
  
  return (
    <Card className="w-full" data-testid="fast-ocr-import">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileSearch className="h-5 w-5 text-blue-600" />
              Fast OCR Import
            </CardTitle>
            <CardDescription>
              Quickly populate inspection fields from documents and photos
            </CardDescription>
          </div>
          {results && (
            <Button variant="outline" size="sm" onClick={reset}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Start Over
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="upload" className="flex items-center gap-1">
              <Upload className="h-4 w-4" />
              Upload
            </TabsTrigger>
            <TabsTrigger value="results" disabled={!results} className="flex items-center gap-1">
              <Eye className="h-4 w-4" />
              Results
              {results?.fields_count > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {results.fields_count}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          {/* Upload Tab */}
          <TabsContent value="upload" className="space-y-4">
            {/* Drop Zone */}
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input')?.click()}
              data-testid="drop-zone"
            >
              <input
                id="file-input"
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <div className="flex flex-col items-center gap-2">
                <div className="flex gap-2">
                  <ImageIcon className="h-8 w-8 text-gray-400" />
                  <FileText className="h-8 w-8 text-gray-400" />
                  <Camera className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-lg font-medium text-gray-700">
                  Drop files here or click to upload
                </p>
                <p className="text-sm text-gray-500">
                  Supports: Inspection sheets, PDFs, Site photos (max 10 files, 20MB each)
                </p>
              </div>
            </div>
            
            {/* Selected Files */}
            {files.length > 0 && (
              <div className="space-y-2">
                <Label>Selected Files ({files.length})</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {files.map((file, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        {getFileIcon(file.type)}
                        <div>
                          <p className="text-sm font-medium truncate max-w-xs">{file.name}</p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Options */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Extraction Mode</Label>
                <Select value={extractionMode} onValueChange={setExtractionMode}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">
                      <span className="flex items-center gap-1">
                        <Sparkles className="h-4 w-4" /> Auto-detect
                      </span>
                    </SelectItem>
                    <SelectItem value="inspection_sheet">Inspection Sheet</SelectItem>
                    <SelectItem value="site_photo">Site Photo</SelectItem>
                    <SelectItem value="pdf_report">PDF Report</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-end">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="use-ai"
                    checked={useAI} 
                    onCheckedChange={setUseAI}
                  />
                  <Label htmlFor="use-ai" className="flex items-center gap-1 cursor-pointer">
                    <Sparkles className="h-4 w-4 text-purple-500" />
                    AI-Enhanced Extraction
                  </Label>
                </div>
              </div>
            </div>
            
            {/* Process Button */}
            <div className="flex justify-end gap-2">
              <Button
                onClick={processFiles}
                disabled={files.length === 0 || processing}
                className="min-w-32"
                data-testid="process-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-1" />
                    Extract Data
                  </>
                )}
              </Button>
            </div>
            
            {/* Progress */}
            {processing && (
              <div className="space-y-2">
                <Progress value={progress} className="h-2" />
                <p className="text-sm text-center text-gray-500">
                  {progress < 30 ? 'Uploading...' : 
                   progress < 80 ? 'Analyzing document...' : 
                   'Finalizing...'}
                </p>
              </div>
            )}
            
            {/* Error */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-700">Extraction Failed</p>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            )}
          </TabsContent>
          
          {/* Results Tab */}
          <TabsContent value="results" className="space-y-4">
            {results && (
              <>
                {/* Summary */}
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-700">
                      Extraction Complete
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Fields Extracted:</span>
                      <span className="ml-1 font-medium">{results.fields_count || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Confidence:</span>
                      <span className="ml-1 font-medium">{results.confidence || results.mapped_fields?.extraction_confidence || 'N/A'}%</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Method:</span>
                      <span className="ml-1 font-medium">
                        {results.ai_result?.success ? 'AI + OCR' : 'OCR'}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Extracted Fields */}
                <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Extracted Fields
                  </h4>
                  
                  {results.mapped_fields && Object.keys(results.mapped_fields).length > 0 ? (
                    <div className="space-y-1">
                      <ExtractedField label="Property Address" value={results.mapped_fields.property_address} />
                      <ExtractedField label="Postcode" value={results.mapped_fields.postcode} />
                      <ExtractedField label="Client Name" value={results.mapped_fields.client_name} />
                      <ExtractedField label="Property Type" value={results.mapped_fields.property_type} />
                      <ExtractedField label="Bedrooms" value={results.mapped_fields.bedrooms} />
                      <ExtractedField label="Bathrooms" value={results.mapped_fields.bathrooms} />
                      <ExtractedField label="Floor Area (sqm)" value={results.mapped_fields.floor_area_sqm} />
                      <ExtractedField label="Tenure" value={results.mapped_fields.tenure} />
                      <ExtractedField label="Year Built" value={results.mapped_fields.year_built} />
                      <ExtractedField label="Market Value" value={results.mapped_fields.market_value ? `£${results.mapped_fields.market_value.toLocaleString()}` : null} />
                      <ExtractedField label="Rental Value (PA)" value={results.mapped_fields.rental_value_pa ? `£${results.mapped_fields.rental_value_pa.toLocaleString()}` : null} />
                      <ExtractedField label="EPC Rating" value={results.mapped_fields.epc_rating} />
                      <ExtractedField label="Council Tax Band" value={results.mapped_fields.council_tax_band} />
                      <ExtractedField label="Inspection Date" value={results.mapped_fields.inspection_date} />
                      <ExtractedField label="Inspector" value={results.mapped_fields.inspector_name} />
                      <ExtractedField label="Overall Condition" value={results.mapped_fields.overall_condition} />
                      
                      {/* Defects if any */}
                      {results.mapped_fields.defects?.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <h5 className="font-medium text-sm mb-2">Defects Identified ({results.mapped_fields.defects.length})</h5>
                          {results.mapped_fields.defects.map((defect, idx) => (
                            <div key={idx} className="p-2 bg-yellow-50 rounded mb-2 text-sm">
                              <p className="font-medium">{defect.location}</p>
                              <p className="text-gray-600">{defect.description}</p>
                              <Badge variant="outline" className="mt-1">{defect.severity}</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No fields extracted</p>
                  )}
                </div>
                
                {/* Raw Text Preview */}
                {results.raw_text && (
                  <details className="border rounded-lg">
                    <summary className="p-3 cursor-pointer text-sm font-medium text-gray-600 hover:bg-gray-50">
                      View Raw OCR Text
                    </summary>
                    <pre className="p-3 text-xs bg-gray-50 max-h-40 overflow-auto whitespace-pre-wrap">
                      {results.raw_text}
                    </pre>
                  </details>
                )}
                
                <Separator />
                
                {/* Actions */}
                <div className="flex justify-end gap-2">
                  {existingInspectionId ? (
                    <Button
                      onClick={populateInspection}
                      disabled={processing || !results?.mapped_fields}
                      data-testid="populate-btn"
                    >
                      {processing ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <ArrowRight className="h-4 w-4 mr-1" />
                      )}
                      Populate Inspection
                    </Button>
                  ) : (
                    <Button
                      onClick={createInspection}
                      disabled={processing || !results?.mapped_fields}
                      data-testid="create-inspection-btn"
                    >
                      {processing ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-1" />
                      )}
                      Create New Inspection
                    </Button>
                  )}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
